import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MessageSquareText, Users, FileText, Wallet, ShieldCheck, Tag, BarChart3, LogOut, Languages, Sparkles, Send, ChevronDown, ChevronRight, LayoutTemplate, Building2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useI18n } from "../../i18n";

const navItemBase = "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-bold uppercase transition-colors";

export default function Sidebar({ onClose }) {
    const { user, logout } = useAuth();
    const { t, lang, toggleLang } = useI18n();
    const navigate = useNavigate();
    const isAdmin = user?.role === "super_admin";
    const [modulesOpen, setModulesOpen] = useState(true);
    const [pivaAdeOpen, setPivaAdeOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [paymentsOpen, setPaymentsOpen] = useState(false);
    const [paymentCreditsOpen, setPaymentCreditsOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    return (
        <aside data-testid="app-sidebar" className="w-72 shrink-0 h-screen sticky top-0 border-e border-border bg-background-alt/70 backdrop-blur-sm flex flex-col">
            {/* Brand */}
            <div className="px-5 pt-6 pb-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-base">F</div>
                    <div>
                        <div className="font-display text-lg font-semibold leading-tight">ComUnica Lab</div>
                        <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{t.app.tagline}</div>
                    </div>
                </div>
            </div>

            {/* User card */}
            <div className="px-4 py-3 mt-2">
                <div className="rounded-md bg-card border border-border px-3 py-2.5">
                    <div className="text-sm font-medium text-foreground truncate">{user?.full_name || user?.email}</div>
                    <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    {!isAdmin && (
                        <div data-testid="sidebar-credits-balance" className="mt-2 flex items-center gap-1.5 text-xs">
                            <Sparkles size={12} className="text-primary" />
                            <span className="text-muted-foreground">{t.common.creditsLeft}:</span>
                            <span className="font-semibold text-foreground">{Number(user?.credits_balance || 0).toFixed(2)}</span>
                        </div>
                    )}
                    {isAdmin && (
                        <div className="mt-1 text-[10px] uppercase tracking-wider font-semibold text-primary">Super Admin</div>
                    )}
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
                <NavItem to="/chat" testid="nav-chat" icon={<MessageSquareText size={16} />} label={t.nav.chat} onClick={onClose} />
                <NavItem to="/comunica/catalogo" testid="nav-comunica-catalog" icon={<Building2 size={16} />} label="Catalogo ComUnica" onClick={onClose} />
                <NavItem to="/clients" testid="nav-clients" icon={<Users size={16} />} label={t.nav.clients} onClick={onClose} />
                <div data-testid="nav-moduli-tree" className="pt-1">
                    <button
                        type="button"
                        data-testid="nav-moduli-toggle"
                        onClick={() => setModulesOpen((v) => !v)}
                        className={`${navItemBase} w-full justify-between bg-primary text-white tracking-wide hover:bg-orange-600`}
                    >
                        <span className="flex items-center gap-3">
                            <FileText size={16} className="text-white" />
                            <span>{t.nav.pratiche}</span>
                        </span>
                        {modulesOpen ? <ChevronDown size={15} className="text-white" /> : <ChevronRight size={15} className="text-white" />}
                    </button>
                    {modulesOpen && (
                        <div className="ms-2 border-l-2 border-orange-300 ps-2 space-y-0.5 pt-1">
                            <div data-testid="nav-piva-ade-tree" className="rounded-md overflow-hidden">
                                <button
                                    type="button"
                                    data-testid="nav-piva-ade-toggle"
                                    onClick={() => setPivaAdeOpen((v) => !v)}
                                    className={`${navItemBase} w-full justify-between bg-primary/90 text-white text-xs hover:bg-orange-600`}
                                >
                                    <span className="flex items-center gap-3">
                                        <FileText size={14} className="text-white" />
                                        <span>{t.nav.pivaAde}</span>
                                    </span>
                                    {pivaAdeOpen ? <ChevronDown size={14} className="text-white" /> : <ChevronRight size={14} className="text-white" />}
                                </button>
                                {pivaAdeOpen && (
                                    <div className="ms-4 border-l-2 border-white/80 ps-2 pb-1 space-y-0.5 bg-primary/90">
                                        <NavItem to="/pratiche" testid="nav-piva-ade-to-send" icon={<FileText size={12} />} label={t.nav.toSend} onClick={onClose} nested small />
                                        <NavItem to="/pratiche?tab=sent" testid="nav-piva-ade-sent" icon={<Send size={12} />} label={t.nav.sent} onClick={onClose} nested small />
                                        <NavItem to="/pratiche?tab=templates" testid="nav-piva-ade-templates" icon={<LayoutTemplate size={12} />} label={t.nav.templates} onClick={onClose} nested small />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <div data-testid="nav-account-tree" className="pt-1">
                    <button
                        type="button"
                        data-testid="nav-account-toggle"
                        onClick={() => setAccountOpen((v) => !v)}
                        className={`${navItemBase} w-full justify-between bg-primary text-white tracking-wide hover:bg-orange-600`}
                    >
                        <span className="flex items-center gap-3">
                            <Users size={16} className="text-white" />
                            <span>{t.nav.account}</span>
                        </span>
                        {accountOpen ? <ChevronDown size={15} className="text-white" /> : <ChevronRight size={15} className="text-white" />}
                    </button>
                    {accountOpen && (
                        <div className="ms-2 border-l-2 border-orange-300 ps-2 space-y-0.5 pt-1">
                            <button type="button" data-testid="nav-account-profile" className={`${navItemBase} w-full text-xs bg-primary/80 text-white cursor-default`} title="Sezione anagrafica utente futura">
                                <Users size={14} className="text-white" />
                                <span>{t.nav.userProfile}</span>
                            </button>
                            <NavItem to="/account/telemaco" testid="nav-account-telemaco" icon={<FileText size={14} />} label={t.nav.telemaco} onClick={onClose} nested />
                        </div>
                    )}
                </div>
                {!isAdmin && (
                    <NavItem to="/credits" testid="nav-credits" icon={<Wallet size={16} />} label={t.nav.credits} onClick={onClose} />
                )}

                {isAdmin && (
                    <div data-testid="nav-payments-tree" className="pt-1">
                        <button
                            type="button"
                            data-testid="nav-payments-toggle"
                            onClick={() => setPaymentsOpen((v) => !v)}
                            className={`${navItemBase} w-full justify-between bg-primary text-white tracking-wide hover:bg-orange-600`}
                        >
                            <span className="flex items-center gap-3">
                                <Wallet size={16} className="text-white" />
                                <span>{t.nav.payments}</span>
                            </span>
                            {paymentsOpen ? <ChevronDown size={15} className="text-white" /> : <ChevronRight size={15} className="text-white" />}
                        </button>
                        {paymentsOpen && (
                            <div className="ms-2 border-l-2 border-orange-300 ps-2 space-y-0.5 pt-1">
                                <NavItem to="/admin/payments/provider" testid="nav-payments-provider" icon={<Wallet size={14} />} label={t.nav.paymentProvider} onClick={onClose} nested />
                                <NavItem to="/admin/payments/accrediti" testid="nav-payments-accrediti" icon={<Wallet size={14} />} label={t.nav.affiliateCredits} onClick={onClose} nested />
                                <div data-testid="nav-payment-credits-tree" className="rounded-md overflow-hidden">
                                    <button
                                        type="button"
                                        data-testid="nav-payment-credits-toggle"
                                        onClick={() => setPaymentCreditsOpen((v) => !v)}
                                        className={`${navItemBase} w-full justify-between bg-primary/90 text-white text-xs hover:bg-orange-600`}
                                    >
                                        <span className="flex items-center gap-3">
                                            <Tag size={14} className="text-white" />
                                            <span>{t.nav.paymentCredits}</span>
                                        </span>
                                        {paymentCreditsOpen ? <ChevronDown size={14} className="text-white" /> : <ChevronRight size={14} className="text-white" />}
                                    </button>
                                    {paymentCreditsOpen && (
                                        <div className="ms-4 border-l-2 border-white/80 ps-2 pb-1 space-y-0.5 bg-primary/90">
                                            <NavItem to="/admin/payments/crediti-generali" testid="nav-payment-credit-general" icon={<Tag size={12} />} label={t.nav.creditGeneralSettings} onClick={onClose} nested small />
                                            <NavItem to="/admin/payments/tariffario" testid="nav-payment-tariffario" icon={<Tag size={12} />} label={t.nav.tariffario} onClick={onClose} nested small />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {isAdmin && (
                    <>
                        <div className="mt-4 mb-1.5 px-3 text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{t.nav.admin}</div>
                        <NavItem to="/admin/stats" testid="nav-admin-stats" icon={<BarChart3 size={16} />} label={t.nav.stats} onClick={onClose} />
                        <NavItem to="/admin/affiliates" testid="nav-admin-affiliates" icon={<ShieldCheck size={16} />} label={t.nav.affiliates} onClick={onClose} />
                    </>
                )}
            </nav>

            {/* Footer */}
            <div className="px-3 py-3 border-t border-border space-y-1">
                <button
                    data-testid="language-switcher"
                    onClick={toggleLang}
                    className={`${navItemBase} w-full justify-between bg-primary text-white hover:bg-orange-600`}
                >
                    <span className="flex items-center gap-3"><Languages size={16} /> {lang === "it" ? "Lingua" : "اللغة"}</span>
                    <span className="text-xs font-mono uppercase font-bold text-white">{lang === "it" ? "IT → AR" : "AR → IT"}</span>
                </button>
                <button
                    data-testid="logout-button"
                    onClick={handleLogout}
                    className={`${navItemBase} w-full bg-primary text-white hover:bg-orange-600`}
                >
                    <LogOut size={16} />{t.nav.logout}
                </button>
            </div>
        </aside>
    );
}

function NavItem({ to, icon, label, onClick, testid, nested = false, small = false }) {
    return (
        <NavLink
            to={to}
            data-testid={testid}
            onClick={onClick}
            className={({ isActive }) => `${navItemBase} ${nested ? "text-xs" : ""} ${small ? "py-1.5 ps-2" : ""} ${isActive ? "bg-orange-600 text-white" : "bg-primary text-white hover:bg-orange-600"}`}
        >
            {icon}<span>{label}</span>
        </NavLink>
    );
}
